import React, { useState, useEffect } from "react";
import Tag from "../Tag";

interface TagInputProps {
  label: string;
  name: string;
  placeholder: string;
  value?: string;
  errors?: string;
  onChange?: (e: { target: { value: string } }) => void;
}

const TagInput: React.FC<TagInputProps> = ({
  label,
  name,
  placeholder,
  value,
  errors,
  onChange,
}) => {
  const [tags, setTags] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState("");

  // Parse comma-separated value prop into tags array
  useEffect(() => {
    if (value) {
      const parsedTags = value
        .split(",")
        .map((tag) => tag.trim())
        .filter((tag) => tag.length > 0);
      setTags(parsedTags);
    } else {
      setTags([]);
    }
  }, [value]);

  const updateTags = (newTags: string[]) => {
    setTags(newTags);
    onChange?.({ target: { value: newTags.join(", ") } });
  };

  const addTag = (tagText: string) => {
    const trimmed = tagText.trim();
    if (trimmed.length === 0) return;

    const lowerCaseTags = tags.map((t) => t.toLowerCase());
    if (lowerCaseTags.includes(trimmed.toLowerCase())) return;

    updateTags([...tags, trimmed]);
    setInputValue("");
  };

  const removeTag = (index: number) => {
    updateTags(tags.filter((_, i) => i !== index));
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    if (newValue.includes(",")) {
      const tagText = newValue.replace(",", "");
      addTag(tagText);
    } else {
      setInputValue(newValue);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === "Backspace" && inputValue === "" && tags.length > 0) {
      // Remove last tag when backspace is pressed with empty input
      removeTag(tags.length - 1);
    }
  };

  const handleBlur = () => {
    if (inputValue.trim().length > 0) {
      addTag(inputValue);
    }
  };

  return (
    <div className="flex w-full flex-col gap-2">
      <label className="text-cream-800 dark:text-cream-400 text-sm font-medium">
        {label}
      </label>
      <div className="flex w-full flex-wrap items-center gap-2">
        {tags.map((tag, index) => (
          <Tag key={index} label={tag} onRemove={() => removeTag(index)} />
        ))}
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          placeholder={tags.length === 0 ? placeholder : ""}
          className="border-cream-500 placeholder-cream-600 selection:bg-peach-300 dark:placeholder-cream-500 text-cream-950 dark:bg-cream-800 dark:text-cream-100 w-full min-w-[120px] flex-1 rounded border font-medium"
        />
      </div>
      <input type="hidden" name={name} value={tags.join(", ")} />
      {errors && (
        <span className="text-peach-600 dark:text-peach-400 text-sm font-semibold">
          {errors}
        </span>
      )}
    </div>
  );
};

export default TagInput;
